import { PageHeader, usePageHeader } from '@easydoctor/ui';
import {
  AUTOMATIC_REPLY_NOTICE,
  rejectedAttachmentsNotice,
  SENDER_AUTHENTICATION_NOTICES,
  type UnassociatedMailItem,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useDiscardUnassociatedMail, useLinkUnassociatedMail, useUnassociatedMail } from '../../api/unassociated-mail';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { formatDateTime } from '../../components/ticket';
import { HelpButton } from '../../guida/HelpButton';
import { QuotedText } from '../ticket/QuotedText';
import { ThreadText } from '../ticket/ThreadText';
import { LinkMailDialog } from './LinkMailDialog';

type DialogState =
  { kind: 'closed' } | { kind: 'link'; mail: UnassociatedMailItem } | { kind: 'discard'; mail: UnassociatedMailItem };

type Outcome = { kind: 'collegata'; sender: string; ticketCode: string } | { kind: 'scartata'; sender: string };

function MailCard({
  mail,
  onLink,
  onDiscard,
}: {
  mail: UnassociatedMailItem;
  onLink: () => void;
  onDiscard: () => void;
}) {
  const titleId = useId();
  const notices = [
    mail.senderAuthentication === 'superata' ? null : SENDER_AUTHENTICATION_NOTICES[mail.senderAuthentication],
    mail.automatic ? AUTOMATIC_REPLY_NOTICE : null,
    mail.rejectedAttachments > 0 ? rejectedAttachmentsNotice(mail.rejectedAttachments) : null,
    mail.truncated ? 'Testo troppo lungo: mostrato solo l’inizio.' : null,
  ].filter((notice): notice is string => notice !== null);
  return (
    <Paper component="li" variant="outlined" sx={{ listStyle: 'none', p: 3 }} aria-labelledby={titleId}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography id={titleId} variant="subtitle1" component="h2">
            {mail.subject || '(senza oggetto)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${mail.sender} · ${formatDateTime(mail.receivedAt)} · a ${mail.recipient}`}
          </Typography>
        </Stack>
        {mail.text ? (
          <ThreadText text={mail.text} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nessun testo oltre la citazione dell’email precedente.
          </Typography>
        )}
        {notices.map((notice) => (
          <Typography key={notice} variant="caption" color="text.secondary" role="note">
            {notice}
          </Typography>
        ))}
        {mail.quotedText && <QuotedText text={mail.quotedText} />}
        <Stack direction="row" spacing={2}>
          <Button variant="contained" size="small" onClick={onLink}>
            Collega a un ticket
          </Button>
          <Button color="error" size="small" onClick={onDiscard}>
            Scarta
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Posta non associata (ADR-003, story #42, PAGE-008): le email arrivate sul dominio dei ticket senza
 * un codice riconoscibile. Non aprono ticket (DEC-006): un operatore le collega a un ticket
 * esistente come messaggio dell'utente, oppure le scarta. Per tutto il team (Analisti e Sviluppo).
 * Il testo è quello ripulito dall'API e resta testo semplice: nessun HTML ricevuto è interpretato.
 */
export function PostaNonAssociataPage() {
  usePageHeader({ title: 'Posta non associata' });
  const { data, isPending, isError, error, refetch } = useUnassociatedMail();
  const link = useLinkUnassociatedMail();
  const discard = useDiscardUnassociatedMail();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const close = () => {
    setDialog({ kind: 'closed' });
    link.reset();
    discard.reset();
  };

  const submitLink = (ticketCode: string) => {
    if (dialog.kind !== 'link') return;
    const { mail } = dialog;
    link.mutate(
      { id: mail.id, ticketCode },
      {
        onSuccess: (linked) => {
          close();
          setOutcome({ kind: 'collegata', sender: mail.sender, ticketCode: linked.ticketCode });
        },
      }
    );
  };

  const confirmDiscard = () => {
    if (dialog.kind !== 'discard') return;
    const { mail } = dialog;
    discard.mutate(mail.id, {
      onSuccess: () => {
        close();
        setOutcome({ kind: 'scartata', sender: mail.sender });
      },
    });
  };

  return (
    <Stack spacing={4}>
      <PageHeader
        title="Posta non associata"
        titleAdornment={<HelpButton screen="/posta-non-associata" />}
        description="Email arrivate all’indirizzo dei ticket senza un codice riconoscibile. Non aprono ticket: collegale a un ticket esistente come messaggio dell’utente, oppure scartale."
      />

      {outcome && (
        <Alert severity="success" role="status" onClose={() => setOutcome(null)}>
          {outcome.kind === 'collegata' ? (
            <>
              Email di {outcome.sender} collegata a{' '}
              <Link component={RouterLink} to={`/ticket/${encodeURIComponent(outcome.ticketCode)}`}>
                {outcome.ticketCode}
              </Link>
              .
            </>
          ) : (
            `Email di ${outcome.sender} scartata: testo e oggetto sono cancellati, resta il registro.`
          )}
        </Alert>
      )}

      {isPending && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Caricamento della posta non associata…</Typography>
        </Stack>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Posta non associata non disponibile</AlertTitle>
          {error.message}
        </Alert>
      )}

      {data && data.items.length === 0 && <Typography>Nessuna email da associare.</Typography>}

      {data && data.items.length > 0 && (
        <Stack spacing={2}>
          {data.total > data.items.length && (
            <Typography variant="body2" color="text.secondary">
              {`Mostrate le ${data.items.length} più recenti di ${data.total}.`}
            </Typography>
          )}
          <Stack component="ul" spacing={2} sx={{ m: 0, p: 0 }} aria-label="Email da associare">
            {data.items.map((mail) => (
              <MailCard
                key={mail.id}
                mail={mail}
                onLink={() => setDialog({ kind: 'link', mail })}
                onDiscard={() => setDialog({ kind: 'discard', mail })}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {dialog.kind === 'link' && (
        <LinkMailDialog
          mail={dialog.mail}
          error={link.error?.message ?? null}
          busy={link.isPending}
          onSubmit={submitLink}
          onClose={close}
        />
      )}

      <ConfirmDialog
        open={dialog.kind === 'discard'}
        title="Scarta l’email"
        confirmLabel="Scarta"
        confirmColor="error"
        error={discard.error?.message ?? null}
        busy={discard.isPending}
        onConfirm={confirmDiscard}
        onClose={close}
      >
        {dialog.kind === 'discard' &&
          `L’email di ${dialog.mail.sender} esce dalla lista; testo e oggetto sono cancellati. Restano mittente, istante di arrivo e chi l’ha scartata. Nessuna email parte verso il mittente.`}
      </ConfirmDialog>
    </Stack>
  );
}
